import { Color } from '@persona-id/design-tokens';
import { noop } from 'lodash/fp';
import { useMemo, VFC } from 'react';
import { Link } from 'react-router-dom-v5-compat';

import Banner from 'dashboard/components-lib/Banner';
import { WorkflowBlockStatus } from 'dashboard/models/workflow-block';
import { WorkflowVersionStatus } from 'dashboard/models/workflow-version';

interface WorkflowStatusBannerProps {
  isInRollout: boolean;
  isShowingWorkflowVersions: boolean;
  isVersionArchived: boolean;
  latestEditableVersionUrl: string;
  showWorkflowVersions: () => void;
  versionStatus: WorkflowVersionStatus | WorkflowBlockStatus;
  workflowName: string;
  onClickEditableVersionLink?: () => void;
}

const WorkflowStatusBanner: VFC<WorkflowStatusBannerProps> = ({
  isInRollout,
  isShowingWorkflowVersions,
  isVersionArchived,
  latestEditableVersionUrl,
  showWorkflowVersions,
  versionStatus,
  workflowName,
  onClickEditableVersionLink = noop,
}) => {
  const editableVersionLink = useMemo(() => {
    if (!isShowingWorkflowVersions) {
      return (
        <>
          {' '}
          -{' '}
          <Link
            to={latestEditableVersionUrl}
            replace
            onClick={onClickEditableVersionLink}
            id='workflow-status-banner__link-to-editable-version'
          >
            view the editable version.
          </Link>
        </>
      );
    }
  }, [isShowingWorkflowVersions, latestEditableVersionUrl, onClickEditableVersionLink]);

  const renderStatus = () => {
    if (isInRollout) {
      return (
        <span>
          Workflows are unmodifiable while in a staged rollout. To see Rollout versions, please view{' '}
          <a id='workflow-status-banner__anchor-workflow-versions' onClick={showWorkflowVersions}>
            Version History
          </a>
          .
        </span>
      );
    } else if (isVersionArchived) {
      return (
        <span>
          You are viewing an archived version for <strong>{workflowName}</strong>. This version can
          no longer be edited
          {editableVersionLink}
        </span>
      );
    } else {
      return (
        <span>
          You are viewing a previous {versionStatus} version for <strong>{workflowName}</strong>.
          This version can no longer be edited
          {editableVersionLink}
        </span>
      );
    }
  };

  return <Banner backgroundColor={Color.CoreYellow300}>{renderStatus()}</Banner>;
};

export default WorkflowStatusBanner;
